import React, { useEffect, useMemo, useRef, useState } from 'react';

import debounce from 'lodash/debounce';

import { box, COMMENTARY_HEIGHT, CONTENT_BOTTOM, CONTENT_WIDTH, MARGIN_X, pt } from '../slideGeometry';

import Badge from 'CommonComponents/Badge';

const SAVE_DEBOUNCE_MS = 800;

/**
 * The box the page's `commentary` block places, or the strip at the foot of the slide.
 */
const frameStyle = (frame) => ({
    ...(frame
        ? box(frame.x ?? MARGIN_X, frame.y, frame.width ?? CONTENT_WIDTH, frame.height ?? COMMENTARY_HEIGHT)
        : box(MARGIN_X, CONTENT_BOTTOM - COMMENTARY_HEIGHT, CONTENT_WIDTH, COMMENTARY_HEIGHT)),
    padding: '8px 12px',
});

/**
 * The warning under the text when the export will cut it to fit the box. It
 * never stops the pack being marked final.
 */
const OverflowNotice = ({ commentary }) =>
    commentary.overflows ? (
        <div className="tw-text-red tw-font-bold" role="status" style={{ fontSize: pt(9) }}>
            {$t('Text will be cut in the export')}
        </div>
    ) : null;

/**
 * The box's label, with a Draft badge while a draft nobody has accepted is in
 * the box and a Stale badge when the figures a draft came from have changed.
 */
const Label = ({ commentary }) => (
    <div className="tw-flex tw-items-center tw-gap-2">
        <span className="tw-text-dark-blue tw-font-bold" style={{ fontSize: pt(9) }}>
            {$t('Commentary')}
        </span>
        {commentary.state === 'draft' && <Badge variant="info">{$t('Draft')}</Badge>}
        {(commentary.stale || commentary.draft?.stale) && <Badge variant="warning">{$t('Stale')}</Badge>}
    </div>
);

/**
 * The commentary box, at the foot of a page or where its `commentary` block puts it, typed in place on a draft. It
 * saves as the user pauses and when the field loses focus.
 *
 * The field follows the server's text (a carried-forward commentary arrives
 * with a later preview) until the user types; from then until that text is
 * saved, the local edit wins.
 */
const EditableCommentary = ({ commentary, frame, onSave }) => {
    const serverText = commentary.text || '';

    const [text, setText] = useState(serverText);
    const textRef = useRef(serverText);
    const dirty = useRef(false);
    const lastSaved = useRef(serverText);

    const onSaveRef = useRef(onSave);
    onSaveRef.current = onSave;

    useEffect(() => {
        if (dirty.current) {
            return;
        }

        lastSaved.current = serverText;
        textRef.current = serverText;
        setText(serverText);
    }, [serverText]);

    const save = useMemo(
        () =>
            debounce((value) => {
                if (value === lastSaved.current) {
                    dirty.current = textRef.current !== value;
                    return;
                }

                Promise.resolve(onSaveRef.current(commentary.pageKey, value)).then((saved) => {
                    if (!saved) {
                        return;
                    }

                    lastSaved.current = value;

                    if (textRef.current === value) {
                        dirty.current = false;
                    }
                });
            }, SAVE_DEBOUNCE_MS),
        [commentary.pageKey],
    );

    // Flush a pending save rather than drop it when the page changes.
    useEffect(() => () => save.flush(), [save]);

    return (
        <div className="tw-bg-blue-10 tw-flex tw-flex-col" style={frameStyle(frame)}>
            <label htmlFor={`mp-commentary-${commentary.pageKey}`} className="tw-mb-0">
                <Label commentary={commentary} />
            </label>
            <textarea
                id={`mp-commentary-${commentary.pageKey}`}
                className="tw-flex-1 tw-w-full tw-resize-none tw-border-0 tw-bg-transparent tw-p-0 tw-text-charcoal focus:tw-ring-1 focus:tw-ring-blue"
                style={{ fontSize: pt(10) }}
                value={text}
                placeholder={$t('Add commentary for this page')}
                onChange={(event) => {
                    const value = event.target.value;

                    dirty.current = true;
                    textRef.current = value;
                    setText(value);
                    save(value);
                }}
                onBlur={() => save.flush()}
            />
            <OverflowNotice commentary={commentary} />
        </div>
    );
};

const CommentaryBlock = ({ commentary, frame, editable, onSave }) => {
    // Space is reserved on every slide of a page; the text belongs to the first.
    if (commentary.primary === false) {
        return null;
    }

    if (editable) {
        return <EditableCommentary key={commentary.pageKey} commentary={commentary} frame={frame} onSave={onSave} />;
    }

    const text = (commentary.text || '').trim();

    if (!text) {
        return null;
    }

    return (
        <div className="tw-bg-blue-10 tw-overflow-hidden" style={frameStyle(frame)}>
            <Label commentary={commentary} />
            <div className="tw-text-charcoal tw-whitespace-pre-line" style={{ fontSize: pt(10) }}>
                {text}
            </div>
            <OverflowNotice commentary={commentary} />
        </div>
    );
};

export default CommentaryBlock;
